import React from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";

import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import { Partner, PartnerMember, PartnerResource } from "../../api/partners";
import { GroupResource } from "../../api/groups";
import { useGroupsStore } from "../../store/groupsStore";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useSnackbar } from "../../hooks/useSnackbar";
import { downloadMembersCsv } from "../../utils/memberCsv";
import DelayedInput from "../common/DelayedInput";
import { InfinitePaginationTable } from "../common/InfinitePaginationTable";
import { MemberBadges } from "./MemberBadges";

type MemberRow = PartnerMember & { id: string };

const PAGE_SIZE = 10;

interface PartnerMembersListDialogProps {
  open: boolean;
  partner: Partner;
  onClose: () => void;
}

/**
 * Read-only member roster for a partner: paginated, searchable by name,
 * email, group, or id. Member management (add/offboard/bulk import) lives in
 * PartnerMembersDialog, reached through the partner details.
 */
export const PartnerMembersListDialog: React.FC<
  PartnerMembersListDialogProps
> = ({ open, partner, onClose }) => {
  const { showSnackbar, SnackbarNode } = useSnackbar();
  const { groups } = useGroupsStore();
  const isMobile = useIsMobile();

  const [members, setMembers] = React.useState<PartnerMember[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [filter, setFilter] = React.useState("");
  const [fetchedGroupName, setFetchedGroupName] = React.useState<string>();

  // Every member belongs to the partner's backing group today; resolve its
  // name for the Group column (and the search index).
  const storeGroup = groups.find((g) => g.id === partner.groupId);
  const groupName = storeGroup?.name ?? fetchedGroupName;

  React.useEffect(() => {
    if (!open) return;
    setFilter("");
    setLoading(true);
    PartnerResource.listMembers(partner.id)
      .then(setMembers)
      .catch((err) =>
        showSnackbar(
          err instanceof Error ? err.message : "Failed to load members.",
          "error",
        ),
      )
      .finally(() => setLoading(false));
  }, [open, partner.id, showSnackbar]);

  React.useEffect(() => {
    if (!open || groupName) return;
    // Only the group NAME is needed here — the lightweight list (name + count)
    // covers it. Kept in LOCAL state, not written to the groups store: a store
    // write would replace enriched group details another open view relies on.
    let cancelled = false;
    GroupResource.getList()
      .then((list) => {
        if (cancelled) return;
        setFetchedGroupName(list.find((g) => g.id === partner.groupId)?.name);
      })
      .catch(() => {
        /* Group column falls back to "—" */
      });
    return () => {
      cancelled = true;
    };
  }, [open, groupName, partner.groupId]);

  const handleFilterChange = React.useCallback((value: string) => {
    setFilter(value);
  }, []);

  // Export the full roster (not just the current search) as CSV.
  const handleExportCsv = React.useCallback(() => {
    downloadMembersCsv(members, groupName ?? "", partner.displayName);
  }, [members, groupName, partner.displayName]);

  const query = filter.trim().toLowerCase();
  const filtered = query
    ? members.filter((m) =>
        [
          m.displayName,
          m.email,
          m.appUserId,
          m.revenueCatId,
          groupName,
          partner.groupId,
        ].some((field) => field?.toLowerCase().includes(query)),
      )
    : members;

  const memberRows: MemberRow[] = filtered.map((m) => ({
    ...m,
    id: m.appUserId,
  }));

  const columns = [
    {
      id: "displayName",
      label: "Name",
      render: (member: MemberRow) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexWrap: "wrap",
          }}
        >
          <span>{member.displayName || "—"}</span>
          <MemberBadges member={member} />
        </Box>
      ),
    },
    {
      id: "email",
      label: "Email",
    },
    {
      id: "appUserId",
      label: "User ID",
      hideOnMobile: true,
      render: (member: MemberRow) => (
        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
          {member.appUserId}
        </Typography>
      ),
    },
    {
      id: "group",
      label: "Group",
      hideOnMobile: true,
      render: () => groupName ?? "—",
    },
    {
      id: "createdAt",
      label: "Created",
      hideOnMobile: true,
      render: (member: MemberRow) =>
        member.createdAt
          ? new Date(member.createdAt).toLocaleDateString()
          : "—",
    },
    {
      id: "groupId",
      label: "Group ID",
      hideOnMobile: true,
      render: () => (
        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
          {partner.groupId}
        </Typography>
      ),
    },
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <span>Members — {partner.displayName}</span>
          <IconButton size="small" onClick={onClose} sx={{ flexShrink: 0 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <DelayedInput
            size="small"
            fullWidth
            label="Search by name, email, group, or id"
            value={filter}
            onDelayedChange={handleFilterChange}
            sx={{ mb: 2 }}
          />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : filtered.length === 0 ? (
            <Typography
              variant="body2"
              color="text.disabled"
              sx={{ py: 2, textAlign: "center" }}
            >
              {query ? "No members match the search." : "No members yet."}
            </Typography>
          ) : (
            // Keyed by the query so a new search always lands on page 1.
            <InfinitePaginationTable
              key={query}
              columns={columns}
              items={memberRows}
              pageSize={PAGE_SIZE}
              onLoadNext={() => {}}
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={handleExportCsv}
            disabled={loading || members.length === 0}
          >
            Export CSV
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {SnackbarNode}
    </>
  );
};
